import React, { useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { Controller, useFormContext } from 'react-hook-form';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { confirmDialog } from 'primereact/confirmdialog';
import { Auth, Storage } from 'aws-amplify';

import { getTenantDetails } from 'utils/utils';
import { FILEUPLOAD_S3, FILEUPLOAD_S3_LEVEL, SetS3Config } from 'config/amplify_config';
import { uuidv4 } from 'common/uuidGenerator';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

export default function AttachmentInput({ index, name = 'attachments', multiple = true }) {
  const { control, setError, clearErrors } = useFormContext();
  const { addToast } = useToast();
  const [tenantId] = getTenantDetails();
  const fieldName = `actions[${index}].${name}`;
  const [uploadingFiles, setUploadingFiles] = useState({});
  const maxFiles = 20;

  const uploadFileToS3 = async (file, fileKey) => {
    try {
      SetS3Config(FILEUPLOAD_S3, FILEUPLOAD_S3_LEVEL);

      const fileObj = {
        progress: 0,
        uploading: true,
        error: null,
      };

      // Add to uploading state (safe functional update)
      setUploadingFiles((prev) => ({ ...prev, [fileKey]: fileObj }));

      // Upload to S3
      await Storage.put(fileKey, file, {
        progressCallback(progress) {
          const percent = Math.round((progress.loaded / progress.total) * 100);
          // Update uploadingFiles state
          setUploadingFiles((prev) => ({
            ...prev,
            [fileKey]: { ...prev[fileKey], progress: percent },
          }));
        },
        contentType: file.type,
      });

      // Upload success — remove from uploading list
      setUploadingFiles((prev) => {
        const updated = { ...prev };
        delete updated[fileKey];
        return updated;
      });
    } catch (err) {
      console.error('Upload error:', err);

      setUploadingFiles((prev) => {
        const updated = { ...prev };
        delete updated[fileKey];
        return updated;
      });

      setError(fieldName, {
        type: 'upload_error',
        message: `Failed to upload ${file.name}`,
      });
    }
  };

  // Monitor uploads to manage field validation
  useEffect(() => {
    const hasUploading = Object.keys(uploadingFiles).length > 0;
    if (!hasUploading) {
      clearErrors(fieldName);
    } else {
      setError(fieldName, {
        type: 'uploading',
        message: 'Please wait for file uploads to complete',
      });
    }
  }, [uploadingFiles, setError, clearErrors, fieldName]);

  const confirmDelete = (file, handleRemoveFile) => {
    confirmDialog({
      message: 'Are you sure you want to delete this file?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        handleRemoveFile(file?.file_path?.replace('public/', ''));
      },
    });
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      defaultValue={[]}
      rules={{
        validate: {
          uploading: () => {
            const hasUploading = Object.keys(uploadingFiles).length > 0;
            return !hasUploading || 'Please wait for file uploads to complete';
          },
        },
      }}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const files = Array.isArray(value) ? value : [];

        const handleRemoveFile = async (key) => {
          if (!key) return;

          try {
            await Storage.remove(key);
          } catch (err) {
            console.error('S3 delete error:', err);
          }
          onChange(files.filter((f) => f.file_path !== 'public/' + key));
        };

        const handleFileDrop = async (acceptedFiles) => {
          if (!acceptedFiles?.length) return;

          const totalFiles = [...files, ...acceptedFiles];
          if (totalFiles.length > maxFiles) {
            if (!handleFileDrop.toastShown) {
              addToast(
                false,
                toastConstant.toasterType.WARN,
                toastConstant.header.WARNINIG,
                toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED
              );
            }
            handleFileDrop.toastShown = true;
            setTimeout(() => (handleFileDrop.toastShown = false), 1000);
            return;
          }

          const user = await Auth.currentAuthenticatedUser();
          const userId = user.attributes.sub;

          const filesToUpload = acceptedFiles?.map((file) => {
            const fileKey = `automation/${tenantId}/${userId}/${uuidv4()}#$#${file.name}`;
            const fileObj = {
              display_name: file.name,
              file_name: file.name,
              file_path: 'public/' + fileKey,
              file_type: file.type,
              size: String(file.size),
            };
            return { fileKey, file, ...fileObj };
          });
          onChange([...files, ...filesToUpload]);
          // Upload all dropped files in parallel
          await Promise.all(
            filesToUpload.map(async (f) => {
              return f?.file?.name ? uploadFileToS3(f?.file, f?.fileKey) : Promise.resolve();
            })
          );
        };

        const isUploading = (fileKey) => uploadingFiles[fileKey];

        const getProgress = (fileKey) => {
          if (!fileKey) return 0;
          return uploadingFiles[fileKey]?.progress ?? 0;
        };

        return (
          <>
            <Dropzone
              multiple={multiple}
              onDrop={handleFileDrop}
              accept="application/pdf,application/msword,.docx,.doc,audio/*,video/*,image/*,.heic,.heif,image/heic,image/heif"
            >
              {({ getRootProps, getInputProps, isDragActive }) => (
                <section>
                  <div
                    {...getRootProps()}
                    className={`text-center d-flex flex-column justify-content-center align-items-center file-manager-uploader mb-2 p-4 border border-dashed rounded ${
                      isDragActive ? 'border-primary bg-light' : ''
                    }`}
                  >
                    <input {...getInputProps()} />
                    <i className="icon-doc-add mb-2 pointer" style={{ fontSize: '2rem' }}></i>
                    <p className="F-size14">{isDragActive ? 'Drop files here...' : 'Click or drag files to upload'}</p>
                    {Object.keys(uploadingFiles).length > 0 && (
                      <small className="text-muted">
                        Uploading {Object.keys(uploadingFiles).length} file{Object.keys(uploadingFiles).length > 1 ? 's' : ''}...
                      </small>
                    )}
                  </div>
                </section>
              )}
            </Dropzone>

            {error && <div className="text-danger small mt-1">{error.message}</div>}

            <div className="mt-3">
              {files.length > 0 && (
                <ul className="list-unstyled">
                  {files.map((file, i) => {
                    if (!file) return null;
                    const fileIsUploading = isUploading(file?.fileKey);
                    const fileProgress = getProgress(file?.fileKey);

                    return (
                      <li key={file?.fileKey || i} className="mb-2">
                        <div className="d-flex justify-content-between align-items-center border px-2 py-1 rounded">
                          <div className="d-flex align-items-center">
                            <i className="pi pi-file me-2"></i>
                            <span>{file?.display_name || 'Unknown file'}</span>
                            {fileIsUploading && <small className="text-muted ms-2">({fileProgress}%)</small>}
                            {!fileIsUploading && fileProgress === 100 && <i className="pi pi-check-circle text-success ms-2"></i>}
                          </div>
                          <Button
                            tooltip="Delete"
                            tooltipOptions={{ showDelay: 500 }}
                            icon="pi pi-trash"
                            className="p-button-rounded p-button-text text-danger"
                            onClick={() => confirmDelete(file, handleRemoveFile)}
                            disabled={fileIsUploading}
                          />
                        </div>
                        {fileIsUploading && <ProgressBar value={fileProgress} className="mt-1" style={{ height: '4px' }} />}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        );
      }}
    />
  );
}
